import { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import {
  Video, Plus, Edit, Trash2, Eye, EyeOff, Save,
  Youtube, Play, Link as LinkIcon, Palette, Users, Check, X,
  ChevronUp, ChevronDown, RefreshCw, ExternalLink, BarChart3, Code
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const TIERS = [
  { value: 'free', label: 'Free Users' },
  { value: 'founding_member', label: 'Founding Members' },
  { value: 'premium', label: 'Premium Users' },
];

// Default UnCloned admin theme
const DEFAULT_THEME = {
  primary: '#df1283',
  secondary: '#FF7901',
  accent: '#0FFFE2',
  success: '#10B981',
  warning: '#EAB308',
  error: '#EF4444',
  background: '#2D063E',
  backgroundSecondary: '#4A0A66',
  backgroundTertiary: '#1a0527',
  text: '#FFFFFF',
  textSecondary: '#9CA3AF',
  border: '#374151',
};

/**
 * Unified Video Embeds Manager
 * Used by both Admin Dashboard and B2B Client Portal
 * 
 * @param {Object} props
 * @param {Object} props.theme - Theme colors (optional, uses default if not provided)
 * @param {boolean} props.isAdmin - If true, uses admin endpoints and shows tier selection
 * @param {boolean} props.isPreviewMode - If true, disables all edit actions
 * @param {boolean} props.showTierSelector - Whether to show tier visibility selector (admin only)
 * @param {boolean} props.showAnalytics - Whether to show analytics summary
 * @param {boolean} props.isB2B - If true, uses solid colors instead of gradients
 */
const VideoEmbedsManagerUnified = ({ 
  theme = null, 
  isAdmin = true, 
  isPreviewMode = false,
  showTierSelector = true,
  showAnalytics = true,
  isB2B = false
}) => {
  const [embeds, setEmbeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEmbed, setEditingEmbed] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  // Merge provided theme with defaults
  const t = theme ? {
    primary: theme.primary_color || theme.primary || DEFAULT_THEME.primary,
    secondary: theme.secondary_color || theme.secondary || DEFAULT_THEME.secondary,
    accent: theme.accent_color || theme.accent || DEFAULT_THEME.accent,
    success: theme.success_color || DEFAULT_THEME.success,
    warning: theme.warning_color || DEFAULT_THEME.warning,
    error: theme.error_color || DEFAULT_THEME.error,
    background: theme.background_primary || theme.background || DEFAULT_THEME.background,
    backgroundSecondary: theme.background_secondary || DEFAULT_THEME.backgroundSecondary,
    backgroundTertiary: theme.background_tertiary || DEFAULT_THEME.backgroundTertiary,
    text: theme.text_primary || theme.text || DEFAULT_THEME.text,
    textSecondary: theme.text_secondary || DEFAULT_THEME.textSecondary,
    border: theme.border_color || DEFAULT_THEME.border,
  } : DEFAULT_THEME;

  // API endpoints based on context
  const API_ENDPOINT = isAdmin ? '/api/admin/video-embeds' : '/api/client-admin/video-embeds';

  // Preset colors - solid colors for B2B, gradients for admin
  const PRESET_COLORS = isB2B ? [
    { value: t.primary, label: 'Brand Color (Default)' },
    { value: t.secondary, label: 'Secondary' },
    { value: '#10B981', label: 'Green' },
    { value: '#3B82F6', label: 'Blue' },
    { value: '#8B5CF6', label: 'Purple' },
  ] : [
    { value: `linear-gradient(135deg, ${t.primary} 0%, ${t.secondary} 100%)`, label: 'Brand Gradient (Default)' },
    { value: t.primary, label: 'Primary' },
    { value: t.secondary, label: 'Secondary' },
    { value: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', label: 'Green' },
    { value: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)', label: 'Blue' },
    { value: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', label: 'Purple' },
  ];

  // Default CTA color based on context
  const defaultCtaColor = isB2B ? t.primary : `linear-gradient(135deg, ${t.primary} 0%, ${t.secondary} 100%)`;

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    platform: 'youtube',
    cta_text: '',
    cta_link: '',
    cta_color: defaultCtaColor,
    visible_tiers: ['free', 'founding_member', 'premium'],
    enabled: true,
  });

  useEffect(() => {
    if (!isPreviewMode) {
      fetchEmbeds();
      if (showAnalytics && isAdmin) {
        fetchAnalytics();
      }
    } else {
      // Sample data for preview mode
      setEmbeds([{
        id: 'preview-1',
        embed_id: 'preview-1',
        title: 'Welcome Video',
        url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        platform: 'youtube',
        cta_text: 'Learn More',
        cta_link: 'https://example.com',
        cta_color: defaultCtaColor,
        visible_tiers: ['free', 'founding_member', 'premium'],
        enabled: true
      }]);
      setLoading(false);
    }
  }, [isPreviewMode, defaultCtaColor]);

  const fetchEmbeds = async () => {
    try {
      const res = await axios.get(`${API_BASE}${API_ENDPOINT}`, { withCredentials: true });
      setEmbeds(res.data.video_embeds || []);
    } catch (error) {
      console.error('Failed to fetch video embeds:', error);
      // Only show error if it's not a 403 (feature disabled) and not a 401 (will redirect to login)
      if (error.response?.status === 401) {
        // Session expired - let the parent component handle this
        console.log('Session expired, redirecting to login...');
      } else if (error.response?.status !== 403) {
        const errorMsg = error.response?.data?.detail || 'Failed to load video embeds';
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API_BASE}${API_ENDPOINT}/analytics`, { withCredentials: true });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.url) {
      toast.error('Video URL is required');
      return;
    }

    if (isPreviewMode) {
      toast.info('Cannot save in preview mode');
      return;
    }

    setSaving(true);
    try {
      if (editingEmbed) {
        await axios.put(`${API_BASE}${API_ENDPOINT}/${editingEmbed.id || editingEmbed.embed_id}`, 
          formData,
          { withCredentials: true }
        );
        toast.success('Video embed updated!');
      } else {
        await axios.post(`${API_BASE}${API_ENDPOINT}`, 
          formData,
          { withCredentials: true }
        );
        toast.success('Video embed created!');
      }
      
      resetForm();
      fetchEmbeds();
    } catch (error) {
      toast.error('Failed to save video embed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (embed) => {
    if (isPreviewMode) return;
    if (!confirm('Are you sure you want to delete this video embed?')) return;
    
    try {
      await axios.delete(`${API_BASE}${API_ENDPOINT}/${embed.id || embed.embed_id}`, {
        withCredentials: true
      });
      toast.success('Video embed deleted');
      fetchEmbeds();
    } catch (error) {
      toast.error('Failed to delete video embed');
    }
  };

  const handleToggleEnabled = async (embed) => {
    if (isPreviewMode) return;
    
    try {
      await axios.put(`${API_BASE}${API_ENDPOINT}/${embed.id || embed.embed_id}`, 
        { ...embed, enabled: !embed.enabled },
        { withCredentials: true }
      );
      toast.success(embed.enabled ? 'Video disabled' : 'Video enabled');
      fetchEmbeds();
    } catch (error) {
      toast.error('Failed to update video');
    }
  };

  const handleEdit = (embed) => {
    setFormData({
      title: embed.title || '',
      url: embed.url || '',
      platform: embed.platform || 'youtube',
      cta_text: embed.cta_text || '',
      cta_link: embed.cta_link || '',
      cta_color: embed.cta_color || defaultCtaColor,
      visible_tiers: embed.visible_tiers || ['free', 'founding_member', 'premium'],
      enabled: embed.enabled !== false,
    });
    setEditingEmbed(embed);
    setShowForm(true);
  };

  const handleMovePriority = async (embed, direction) => {
    if (isPreviewMode) return;
    
    const currentIndex = embeds.findIndex(e => (e.id || e.embed_id) === (embed.id || embed.embed_id));
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= embeds.length) return;
    
    const newOrder = [...embeds];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    
    try {
      await axios.post(`${API_BASE}${API_ENDPOINT}/reorder`, 
        { order: newOrder.map(e => e.id || e.embed_id) },
        { withCredentials: true }
      );
      setEmbeds(newOrder);
      toast.success('Order updated');
    } catch (error) {
      toast.error('Failed to reorder');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      url: '',
      platform: 'youtube',
      cta_text: '',
      cta_link: '',
      cta_color: defaultCtaColor,
      visible_tiers: ['free', 'founding_member', 'premium'],
      enabled: true,
    });
    setEditingEmbed(null);
    setShowForm(false);
  };

  const toggleTier = (tier) => {
    setFormData(prev => ({
      ...prev,
      visible_tiers: prev.visible_tiers.includes(tier)
        ? prev.visible_tiers.filter(t => t !== tier)
        : [...prev.visible_tiers, tier]
    }));
  };

  // Check if URL is embed code
  const isEmbedCode = (url) => url?.trim().includes('<iframe');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin" style={{ color: t.textSecondary }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Video className="w-6 h-6" style={{ color: t.primary }} />
          <div>
            <h2 className="text-xl font-bold" style={{ color: t.text }}>Video Embeds</h2>
            <p className="text-sm" style={{ color: t.textSecondary }}>
              {isAdmin 
                ? 'Create multiple videos, each targetable to specific tiers'
                : 'Add YouTube or Vimeo videos to display on your users\' dashboard'}
            </p>
          </div>
        </div>
        {!isPreviewMode && (
          <Button 
            onClick={() => { resetForm(); setShowForm(true); }}
            style={{ background: isB2B ? t.primary : `linear-gradient(135deg, ${t.primary}, ${t.secondary})`, color: 'white' }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Video
          </Button>
        )}
      </div>

      {/* Analytics Summary */}
      {showAnalytics && analytics && analytics.total_clicks > 0 && (
        <div 
          className="p-4 rounded-xl"
          style={{ 
            background: `${t.primary}15`,
            border: `1px solid ${t.primary}40`
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-5 h-5" style={{ color: t.primary }} />
            <span className="font-medium" style={{ color: t.text }}>Total CTA Clicks: {analytics.total_clicks}</span>
          </div>
          <p className="text-sm" style={{ color: t.textSecondary }}>Last 30 days across all videos</p>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div 
          className="p-5 rounded-xl space-y-4"
          style={{
            background: `${t.primary}08`,
            border: `1px solid ${t.primary}40`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold" style={{ color: t.text }}>
              {editingEmbed ? 'Edit Video Embed' : 'New Video Embed'}
            </h3>
            <button 
              onClick={resetForm} 
              className="p-1 rounded hover:opacity-80"
              style={{ color: t.textSecondary }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Platform */}
          <div>
            <label className="text-sm mb-2 block" style={{ color: t.textSecondary }}>Platform</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { id: 'youtube', label: 'YouTube', icon: Youtube },
                { id: 'vimeo', label: 'Vimeo', icon: Play },
                { id: 'embed', label: 'Embed Code', icon: Code }
              ].map(platform => (
                <button
                  key={platform.id}
                  onClick={() => setFormData(prev => ({ ...prev, platform: platform.id }))}
                  className="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2"
                  style={{
                    background: formData.platform === platform.id ? t.primary : t.backgroundSecondary,
                    color: formData.platform === platform.id ? 'white' : t.textSecondary
                  }}
                >
                  <platform.icon className="w-4 h-4" />
                  {platform.label}
                </button>
              ))}
            </div>
          </div>

          {/* URL or Embed Code */}
          <div>
            <label className="text-sm mb-2 block" style={{ color: t.textSecondary }}>
              {formData.platform === 'embed' ? 'Embed Code *' : 'Video URL or Embed Code *'}
            </label>
            {formData.platform === 'embed' || isEmbedCode(formData.url) ? (
              <textarea
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                placeholder='<iframe src="https://www.youtube.com/embed/..." width="560" height="315" frameborder="0" allowfullscreen></iframe>'
                rows={4}
                className="w-full px-3 py-2 rounded-md text-sm"
                style={{
                  backgroundColor: t.backgroundTertiary,
                  border: `1px solid ${t.border}`,
                  color: t.text,
                  fontFamily: 'monospace',
                  resize: 'vertical'
                }}
              />
            ) : (
              <Input
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                placeholder={formData.platform === 'youtube' 
                  ? 'https://www.youtube.com/watch?v=...'
                  : 'https://vimeo.com/...'
                }
                style={{
                  backgroundColor: t.backgroundTertiary,
                  borderColor: t.border,
                  color: t.text
                }}
              />
            )}
          </div>

          {/* Title */}
          <div>
            <label className="text-sm mb-2 block" style={{ color: t.textSecondary }}>Title (optional, shown above video)</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Featured Video"
              style={{
                backgroundColor: t.backgroundTertiary,
                borderColor: t.border,
                color: t.text
              }}
            />
          </div>

          {/* CTA Section */}
          <div 
            className="p-4 rounded-lg space-y-3"
            style={{
              background: `${t.backgroundTertiary}80`,
              border: `1px solid ${t.border}`
            }}
          >
            <h4 className="text-sm font-medium flex items-center gap-2" style={{ color: t.text }}>
              <LinkIcon className="w-4 h-4" style={{ color: t.primary }} />
              Call-to-Action Button (optional)
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs mb-1 block" style={{ color: t.textSecondary }}>Button Text</label>
                <Input
                  value={formData.cta_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                  placeholder="Learn More"
                  style={{
                    backgroundColor: t.backgroundSecondary,
                    borderColor: t.border,
                    color: t.text
                  }}
                />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: t.textSecondary }}>Button Link (trackable)</label>
                <Input
                  value={formData.cta_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, cta_link: e.target.value }))}
                  placeholder="https://example.com"
                  style={{
                    backgroundColor: t.backgroundSecondary,
                    borderColor: t.border,
                    color: t.text
                  }}
                />
              </div>
            </div>

            {/* Color picker */}
            <div>
              <label className="text-xs mb-2 block flex items-center gap-1" style={{ color: t.textSecondary }}>
                <Palette className="w-3 h-3" />
                Button Color
              </label>
              <div className="flex flex-wrap gap-2 items-center">
                {PRESET_COLORS.map(color => (
                  <button
                    key={color.value}
                    onClick={() => setFormData(prev => ({ ...prev, cta_color: color.value }))}
                    className={`w-8 h-8 rounded-full transition-all ${
                      formData.cta_color === color.value 
                        ? 'ring-2 ring-white ring-offset-2 scale-110' 
                        : 'hover:scale-105'
                    }`}
                    style={{ 
                      background: color.value,
                      ringOffsetColor: t.backgroundSecondary
                    }}
                    title={color.label}
                  />
                ))}
                {/* Custom color picker */}
                <div className="relative group">
                  <div 
                    className="w-8 h-8 rounded-full border-2 border-dashed flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity overflow-hidden"
                    style={{ 
                      borderColor: t.border,
                      background: formData.cta_color && !PRESET_COLORS.find(c => c.value === formData.cta_color) 
                        ? formData.cta_color 
                        : t.backgroundTertiary
                    }}
                    title="Pick custom color"
                  >
                    <Palette 
                      className="w-4 h-4 transition-colors" 
                      style={{ 
                        color: t.textSecondary,
                        display: formData.cta_color && !PRESET_COLORS.find(c => c.value === formData.cta_color) ? 'none' : 'block' 
                      }} 
                    />
                  </div>
                  <input
                    type="color"
                    value={formData.cta_color?.startsWith('#') ? formData.cta_color : t.primary}
                    onChange={(e) => setFormData(prev => ({ ...prev, cta_color: e.target.value }))}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
                <span className="text-xs ml-1" style={{ color: t.textSecondary }}>Custom</span>
              </div>
            </div>

            {/* Preview */}
            {formData.cta_text && (
              <div className="pt-2">
                <label className="text-xs mb-2 block" style={{ color: t.textSecondary }}>Preview:</label>
                <div 
                  className="flex justify-center p-3 rounded-lg"
                  style={{ background: t.backgroundSecondary }}
                >
                  <button
                    className="px-6 py-2 rounded-xl font-semibold text-white text-sm"
                    style={{ background: formData.cta_color }}
                  >
                    {formData.cta_text}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Tier Selection - Admin Only */}
          {showTierSelector && isAdmin && (
            <div>
              <label className="text-sm mb-2 block flex items-center gap-2" style={{ color: t.textSecondary }}>
                <Users className="w-4 h-4" />
                Visible to:
              </label>
              <div className="flex flex-wrap gap-2">
                {TIERS.map(tier => {
                  const isSelected = formData.visible_tiers.includes(tier.value);
                  return (
                    <button
                      key={tier.value}
                      onClick={() => toggleTier(tier.value)}
                      className="px-3 py-1.5 rounded-full text-sm transition-all flex items-center gap-1.5"
                      style={{
                        background: isSelected ? `${t.success}20` : t.backgroundTertiary,
                        border: `1px solid ${isSelected ? t.success : t.border}`,
                        color: isSelected ? t.success : t.textSecondary
                      }}
                    >
                      {isSelected && <Check className="w-3 h-3" />}
                      {tier.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Enabled toggle */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setFormData(prev => ({ ...prev, enabled: !prev.enabled }))}
              className="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2"
              style={{
                background: formData.enabled ? t.success : t.backgroundTertiary,
                color: formData.enabled ? 'white' : t.textSecondary
              }}
            >
              {formData.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {formData.enabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={resetForm}
              style={{ borderColor: t.border, color: t.textSecondary }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving || isPreviewMode}
              style={{ background: isB2B ? t.primary : `linear-gradient(135deg, ${t.primary}, ${t.secondary})`, color: 'white' }}
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingEmbed ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      )}

      {/* Embeds List */}
      {embeds.length === 0 ? (
        <div 
          className="p-8 rounded-xl text-center"
          style={{
            background: `${t.backgroundSecondary}50`,
            border: `1px solid ${t.border}`
          }}
        >
          <Video className="w-12 h-12 mx-auto mb-3" style={{ color: `${t.textSecondary}50` }} />
          <p style={{ color: t.textSecondary }}>No video embeds yet.</p>
          <p className="text-sm" style={{ color: `${t.textSecondary}80` }}>Click "Add Video" to create your first video embed.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {embeds.map((embed, index) => (
            <div
              key={embed.id || embed.embed_id}
              className="p-4 rounded-xl transition-all"
              style={{
                background: embed.enabled ? `${t.backgroundSecondary}50` : `${t.backgroundSecondary}30`,
                border: `1px solid ${t.border}`,
                opacity: embed.enabled ? 1 : 0.6
              }}
            >
              <div className="flex items-start gap-4">
                {/* Priority controls */}
                {!isPreviewMode && (
                  <div className="flex flex-col items-center gap-1 pt-1">
                    <button
                      onClick={() => handleMovePriority(embed, 'up')}
                      disabled={index === 0}
                      className="p-1 rounded disabled:opacity-30 hover:opacity-80"
                      title="Move up"
                      style={{ color: t.textSecondary }}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-medium" style={{ color: t.textSecondary }}>{index + 1}</span>
                    <button
                      onClick={() => handleMovePriority(embed, 'down')}
                      disabled={index === embeds.length - 1}
                      className="p-1 rounded disabled:opacity-30 hover:opacity-80"
                      title="Move down"
                      style={{ color: t.textSecondary }}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Video info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {embed.platform === 'youtube' ? (
                      <Youtube className="w-4 h-4 text-red-500" />
                    ) : (
                      <Play className="w-4 h-4 text-blue-400" />
                    )}
                    <h4 className="font-medium truncate" style={{ color: t.text }}>
                      {embed.title || 'Untitled Video'}
                    </h4>
                    {!embed.enabled && (
                      <span 
                        className="px-2 py-0.5 rounded text-xs"
                        style={{ background: t.backgroundTertiary, color: t.textSecondary }}
                      >
                        Disabled
                      </span>
                    )}
                  </div>
                  
                  <p className="text-sm truncate mb-2" style={{ color: t.textSecondary }}>{embed.url}</p>
                  
                  {/* Tier badges - Admin only */}
                  {showTierSelector && isAdmin && embed.visible_tiers && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {embed.visible_tiers.map(tier => (
                        <span 
                          key={tier}
                          className="px-2 py-0.5 rounded-full text-xs"
                          style={{ background: t.backgroundTertiary, color: t.textSecondary }}
                        >
                          {TIERS.find(t => t.value === tier)?.label || tier}
                        </span>
                      ))}
                    </div>
                  )}

                  {embed.cta_text && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs" style={{ color: `${t.textSecondary}80` }}>CTA:</span>
                      <span 
                        className="px-3 py-1 rounded-lg text-xs text-white"
                        style={{ background: embed.cta_color }}
                      >
                        {embed.cta_text}
                      </span>
                      {embed.cta_link && (
                        <a 
                          href={embed.cta_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs hover:underline flex items-center gap-1"
                          style={{ color: t.primary }}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Link
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!isPreviewMode && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleEnabled(embed)}
                      className="p-2 rounded-lg transition-colors"
                      style={{
                        background: embed.enabled ? `${t.success}20` : t.backgroundTertiary,
                        color: embed.enabled ? t.success : t.textSecondary
                      }}
                      title={embed.enabled ? 'Disable' : 'Enable'}
                    >
                      {embed.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleEdit(embed)}
                      className="p-2 rounded-lg transition-colors hover:opacity-80"
                      style={{ background: t.backgroundTertiary, color: t.textSecondary }}
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(embed)}
                      className="p-2 rounded-lg transition-colors hover:opacity-80"
                      style={{ background: `${t.error}20`, color: t.error }}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Help text */}
      <div 
        className="p-4 rounded-lg"
        style={{
          background: `${t.backgroundSecondary}50`,
          border: `1px solid ${t.border}`
        }}
      >
        <p className="text-sm" style={{ color: t.textSecondary }}>
          <strong style={{ color: `${t.textSecondary}CC` }}>How it works:</strong> Users will see all enabled videos{isAdmin && ' that match their membership tier'}, 
          displayed in the order shown above. Use the arrows to reorder videos. Each video can have its own CTA button for tracking.
        </p>
      </div>
    </div>
  );
};

export default VideoEmbedsManagerUnified;
